import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Search, Shield, Sparkles, Trash2, Users } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import LiveNow from "@/components/LiveNow";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Admin() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "live" | "dead"
  const [busy, setBusy] = useState(null);
  const [featureTarget, setFeatureTarget] = useState(null);
  const [featureDays, setFeatureDays] = useState(7);
  const navigate = useNavigate();

  // NOTE: do NOT redirect on role !== admin — instead render a clear denied state
  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const load = async () => {
    try {
      const [s, st] = await Promise.all([
        api.get("/admin/servers", {
          params: {
            search: q || undefined,
            status: statusFilter === "all" ? undefined : statusFilter,
            limit: 200,
          },
        }),
        api.get("/admin/stats"),
      ]);
      setItems(s.data.items || []);
      setStats(st.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "owner") load();
  }, [user, statusFilter]);

  const doSearch = (e) => {
    e.preventDefault();
    load();
  };

  // Silent stats refresh (no full-page spinner); keeps the DEAD tile and
  // filter chip counts in sync after any admin mutation.
  const refreshStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      // ignore — the stat card just stays stale until next full load
    }
  };

  const remove = async (s) => {
    setBusy(s.id);
    try {
      await api.delete(`/servers/${s.id}`);
      setItems((prev) => prev.filter((x) => x.id !== s.id));
      toast.success(`Removed ${s.name}`);
      refreshStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  const openFeature = (s) => {
    setFeatureTarget(s);
    setFeatureDays(7);
  };

  const confirmFeature = async () => {
    if (!featureTarget) return;
    const s = featureTarget;
    setBusy(s.id);
    try {
      const { data } = await api.post(`/admin/servers/${s.id}/feature`, { days: featureDays });
      setItems((prev) =>
        prev.map((x) =>
          x.id === s.id ? { ...x, featured_until: data.featured_until, is_featured: true } : x
        )
      );
      toast.success(`${s.name} featured for ${featureDays} day${featureDays === 1 ? "" : "s"}`);
      refreshStats();
      setFeatureTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to feature");
    } finally {
      setBusy(null);
    }
  };

  const unfeature = async (s) => {
    setBusy(s.id);
    try {
      await api.delete(`/admin/servers/${s.id}/feature`);
      setItems((prev) =>
        prev.map((x) =>
          x.id === s.id ? { ...x, featured_until: null, is_featured: false } : x
        )
      );
      toast.success(`Removed featured status from ${s.name}`);
      refreshStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to unfeature");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="pt-32 flex justify-center" data-testid="admin-loading">
        <div className="w-8 h-8 border-2 border-blurple border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  if (user.role !== "admin" && user.role !== "owner") {
    return (
      <div className="pt-32 pb-16 max-w-xl mx-auto px-4 text-center" data-testid="admin-denied">
        <SEO title="Access denied" description="Staff-only area" path="/admin" noindex />
        <Shield className="w-14 h-14 text-red-400 mx-auto" />
        <h1 className="mt-6 font-heading text-3xl font-black">Staff-only area</h1>
        <p className="mt-3 text-sm text-zinc-400">
          Your account (<span className="font-mono text-white">{user.email}</span>) is not on the staff list.
          Contact the site owner if you believe this is a mistake.
        </p>
        <div className="mt-4 text-xs text-zinc-600 font-mono">
          Current role: <span className="text-red-400">{user.role || "user"}</span>
        </div>
        <button
          onClick={() => navigate("/")}
          className="mt-8 h-10 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold"
        >
          Back to home
        </button>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="admin-page">
      <SEO title="Staff console" description="Serverlistr staff console." path="/admin" noindex />
      <Breadcrumbs items={[{ name: "Staff console" }]} />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-2">
            <Shield className="w-3.5 h-3.5" /> Staff-only
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black">Moderation console</h1>
          <p className="mt-2 text-sm text-zinc-400">
            Manage every listing on Serverlistr. Delete spam, review reports, keep the directory clean.
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate("/admin/moderation")}
              data-testid="open-moderation"
              className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/40 text-yellow-300 text-sm font-semibold transition-all"
            >
              🛡️ Moderation Queue
            </button>
            <button
              onClick={() => navigate("/admin/stats")}
              data-testid="open-stats"
              className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-neon-pink/10 hover:bg-neon-pink/20 border border-neon-pink/40 text-neon-pink text-sm font-semibold transition-all"
            >
              📊 Stats
            </button>
            <button
              onClick={() => navigate("/admin/reports")}
              data-testid="open-reports"
              className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 text-red-300 text-sm font-semibold transition-all"
            >
              🚩 Reports
            </button>
            <button
              onClick={() => navigate("/admin/users")}
              data-testid="open-banned-users"
              className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/40 text-purple-300 text-sm font-semibold transition-all"
            >
              🔨 Banned users
            </button>
          </div>
        </div>
        <form onSubmit={doSearch} className="flex-1 max-w-sm md:max-w-xs">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, description, invite code..."
              className="w-full h-11 pl-10 pr-3 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-500 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple transition-all"
            />
          </div>
        </form>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard label="Servers" value={stats.servers} />
          <StatCard label="Dead" value={stats.dead_servers || 0} dead />
          <StatCard label="Users" value={stats.users} />
          <StatCard label="Featured now" value={stats.featured_now} accent />
          <StatCard label="Paid boosts" value={stats.paid_transactions} />
          <StatCard label="Revenue ($)" value={stats.total_revenue.toFixed(2)} accent />
        </div>
      )}

      <div className="mt-6">
        <LiveNow />
      </div>

      {/* Status filter chips — lets staff see live vs dead vs all listings.
          Dead servers are hidden from the public site but stay in the admin
          list for moderation, so an explicit filter is the only way to
          separate them at a glance. */}
      <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="admin-status-filter">
        {[
          { id: "all", label: "All" },
          { id: "live", label: "Live only" },
          { id: "dead", label: `Dead (${stats?.dead_servers ?? 0})`, tone: "red" },
        ].map((chip) => {
          const active = statusFilter === chip.id;
          const base = "h-9 px-4 rounded-lg text-sm font-semibold border transition-all";
          const red = active
            ? "bg-red-500/20 border-red-500/50 text-red-300"
            : "bg-red-500/5 border-red-500/20 text-red-400 hover:bg-red-500/10";
          const plain = active
            ? "bg-white/15 border-white/30 text-white"
            : "bg-white/5 border-white/10 text-zinc-400 hover:bg-white/10 hover:text-white";
          return (
            <button
              key={chip.id}
              type="button"
              onClick={() => setStatusFilter(chip.id)}
              data-testid={`admin-filter-${chip.id}`}
              className={`${base} ${chip.tone === "red" ? red : plain}`}
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 rounded-2xl border border-white/5 bg-[#0A0A0F] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Server</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Owner</th>
              <th className="text-right px-4 py-3">Rating</th>
              <th className="text-right px-4 py-3 hidden sm:table-cell">Members</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr
                key={s.id}
                data-testid="admin-row"
                data-server-dead={s.is_dead ? "true" : "false"}
                className={`border-t border-white/5 hover:bg-white/[0.02] ${
                  s.is_dead ? "opacity-70" : ""
                }`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <img
                      src={s.icon_url || "https://cdn.discordapp.com/embed/avatars/0.png"}
                      alt=""
                      className={`w-9 h-9 rounded-lg object-cover bg-[#12121A] ${
                        s.is_dead ? "grayscale" : ""
                      }`}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/servers/${s.slug || s.id}`} className="font-semibold truncate hover:text-blurple">
                          {s.name}
                        </Link>
                        {s.is_featured && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold text-neon-cyan bg-neon-cyan/10 border border-neon-cyan/30">
                            <Sparkles className="w-2.5 h-2.5" />
                            FTR
                          </span>
                        )}
                        {s.is_dead && (
                          <span
                            data-testid="admin-dead-badge"
                            title="Bot removed or invite dead — hidden from the public site"
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-black tracking-widest text-red-300 bg-red-500/15 border border-red-500/40"
                          >
                            DEAD
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 font-mono truncate">
                        {(s.categories || []).join(" · ")} · #{s.invite_code}
                      </div>
                      <div
                        data-testid={`admin-owner-${s.id}`}
                        className="text-[11px] text-zinc-400 truncate mt-0.5"
                      >
                        by <span className="text-zinc-200 font-semibold">{s.owner?.name || "Unknown"}</span>
                        {s.owner?.email && (
                          <span className="text-zinc-600"> · {s.owner.email}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <div className="text-zinc-300">{s.owner?.name || "—"}</div>
                  <div className="text-xs text-zinc-500 truncate max-w-[220px]">{s.owner?.email || s.owner_id}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {s.review_count > 0
                    ? `${Number(s.avg_rating || 0).toFixed(1)}★ · ${s.review_count}`
                    : "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono hidden sm:table-cell">
                  <span className="inline-flex items-center gap-1 text-zinc-400">
                    <Users className="w-3 h-3" />
                    {(s.member_count || 0).toLocaleString()}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end items-center gap-1">
                    {s.is_featured ? (
                      <button
                        data-testid={`admin-unfeature-${s.id}`}
                        onClick={() => unfeature(s)}
                        disabled={busy === s.id}
                        title="Remove featured status"
                        className="h-8 px-2 rounded-lg bg-neon-cyan/10 hover:bg-neon-cyan/20 border border-neon-cyan/40 text-neon-cyan text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        <Sparkles className="w-3 h-3" /> FTR
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-feature-${s.id}`}
                        onClick={() => openFeature(s)}
                        disabled={busy === s.id}
                        title="Grant featured status"
                        className="h-8 px-2 rounded-lg bg-white/5 hover:bg-neon-cyan/10 hover:text-neon-cyan border border-white/10 hover:border-neon-cyan/30 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        <Sparkles className="w-3 h-3" /> Feature
                      </button>
                    )}
                    <a
                      href={s.invite_url}
                      target="_blank"
                      rel="noreferrer"
                      className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center"
                      title="Open Discord invite"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          data-testid={`admin-delete-${s.id}`}
                          disabled={busy === s.id}
                          className="h-8 w-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 flex items-center justify-center disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0A0A0F] border-white/10 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {s.name}?</AlertDialogTitle>
                          <AlertDialogDescription className="text-zinc-400">
                            This will permanently remove the listing from Serverlistr.
                            The Discord server itself is untouched. This action can&apos;t be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => remove(s)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete listing
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-sm text-zinc-500 py-12">
                  No servers match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!featureTarget} onOpenChange={(o) => !o && setFeatureTarget(null)}>
        <DialogContent className="bg-[#0A0A0F] border-white/10 text-white sm:max-w-md" data-testid="feature-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-neon-cyan" />
              Feature {featureTarget?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Grant this server a featured slot at no cost. Extends the current window if it&apos;s already featured.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-2 mt-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                data-testid={`feature-days-${d}`}
                onClick={() => setFeatureDays(d)}
                className={`h-11 rounded-lg text-sm font-bold border transition-all ${
                  featureDays === d
                    ? "bg-neon-cyan/15 border-neon-cyan text-neon-cyan"
                    : "bg-white/5 border-white/10 text-zinc-300 hover:border-white/20"
                }`}
              >
                {d} days
              </button>
            ))}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Or custom (days)</label>
            <input
              data-testid="feature-days-input"
              type="number"
              min={1}
              max={3650}
              value={featureDays}
              onChange={(e) => {
                const v = e.target.value;
                // Allow empty while typing; commit numeric value if present
                if (v === "") return setFeatureDays("");
                const n = Number(v);
                if (!Number.isNaN(n)) setFeatureDays(n);
              }}
              onBlur={() =>
                setFeatureDays((prev) => {
                  const n = Number(prev);
                  if (!Number.isFinite(n) || n < 1) return 1;
                  if (n > 3650) return 3650;
                  return n;
                })
              }
              className="mt-2 w-full h-11 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple"
            />
          </div>

          <DialogFooter>
            <button
              onClick={() => setFeatureTarget(null)}
              className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              data-testid="feature-confirm-btn"
              onClick={confirmFeature}
              disabled={busy === featureTarget?.id}
              className="h-10 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white text-sm font-bold disabled:opacity-50"
            >
              Feature for {featureDays} day{featureDays === 1 ? "" : "s"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, accent, dead }) {
  const border = dead
    ? "border-red-500/40 bg-red-500/5"
    : accent
      ? "border-neon-cyan/40 bg-neon-cyan/5"
      : "border-white/5 bg-[#0A0A0F]";
  const text = dead ? "text-red-300" : accent ? "text-neon-cyan" : "";
  return (
    <div className={`rounded-xl p-4 border ${border}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`mt-1 font-heading text-xl font-black ${text}`}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
